import React, { useState, useCallback } from 'react';
import {
  Modal,
  Group,
  Text,
  Button,
  Slider,
  Stack,
  ActionIcon,
  Box,
} from '@mantine/core';
import Cropper from 'react-easy-crop';
import { IconRotateClockwise, IconFlipHorizontal } from '@tabler/icons-react';
import { primaryColor } from '../../constants/theme';
import { useApp } from '../../context/AppContext';

interface ImageCropModalProps {
  opened: boolean;
  onClose: () => void;
  imageSrc: string;
  onSave: (croppedImageBase64: string) => void;
}

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  rotation = 0,
  flip = { horizontal: false, vertical: false }
): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // calculate bounding box of the rotated image
  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = {
    width: Math.abs(Math.cos(rotRad) * image.width) + Math.abs(Math.sin(rotRad) * image.height),
    height: Math.abs(Math.sin(rotRad) * image.width) + Math.abs(Math.cos(rotRad) * image.height),
  };

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  // croppedArea format is a bounding box. we need to extract it
  const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

  // Always output at exactly 300x300 (crop is always 1:1 square)
  canvas.width = 300;
  canvas.height = 300;

  // create temporary canvas to hold the raw extracted crop data
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = pixelCrop.width;
  tempCanvas.height = pixelCrop.height;
  const tempCtx = tempCanvas.getContext('2d');
  if (tempCtx) {
    tempCtx.putImageData(data, 0, 0);
  }

  // Scale the crop down/up to exactly 300x300
  ctx.drawImage(
    tempCanvas,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    300,
    300
  );

  return canvas.toDataURL('image/jpeg');
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  opened,
  onClose,
  imageSrc,
  onSave,
}) => {
  const { isTabletAndBelow } = useApp();

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipHorizontal, setFlipHorizontal] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (croppedAreaPixels) {
      const result = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
        { horizontal: flipHorizontal, vertical: false }
      );
      onSave(result);
    }
  };

  const handleRotate90 = () => setRotation((prev) => (prev + 90) % 360);
  const handleFlip = () => setFlipHorizontal(!flipHorizontal);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen={isTabletAndBelow}
      size="lg"
      centered
      withCloseButton={false}
      padding={0}
      styles={{
        inner: { padding: 0 },
        content: { display: 'flex', flexDirection: 'column', backgroundColor: '#FAFAFA' },
        body: { display: 'flex', flexDirection: 'column', flex: 1, padding: 0 },
      }}
    >
      <Box style={{ display: 'flex', flexDirection: 'column', height: isTabletAndBelow ? '100vh' : '80vh', width: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Group justify="space-between" align="center" px="md" py="sm" style={{ backgroundColor: 'white', borderBottom: '1px solid #Eaeaea' }}>
          <Button variant="subtle" color="gray" onClick={onClose} fw="normal" data-testid="crop-cancel-button">
            Cancel
          </Button>
          <Text fw={600} size="lg">
            Edit Image
          </Text>
          <Button variant="subtle" color={primaryColor} onClick={handleSave} fw="normal" data-testid="crop-save-button">
            Save
          </Button>
        </Group>

        {/* Cropper Container */}
        <Box style={{ position: 'relative', flex: 1, minHeight: 0, width: '100%', height: '100%' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            cropShape="round"
            showGrid={true}
            restrictPosition={false}
            transform={`translate(${crop.x}px, ${crop.y}px) rotateZ(${rotation}deg) rotateY(${flipHorizontal ? 180 : 0}deg) scale(${zoom})`}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </Box>

        {/* Bottom Controls */}
        <Box style={{ backgroundColor: '#FAFAFA', padding: '24px 16px', borderTop: '1px solid #Eaeaea' }}>
          <Stack gap="xl">
            {/* Rotation Slider */}
            <Group align="center" justify="space-between" style={{ width: '100%' }}>
              <Text size="xs" fw={500} c="dimmed">-45°</Text>
              <Text size="xs" fw={600} c={primaryColor} style={{ flex: 1, textAlign: 'center' }}>
                {Math.round(rotation)}°
              </Text>
              <Text size="xs" fw={500} c="dimmed">+45°</Text>
            </Group>
            <Box px="md">
            <Slider
              value={rotation}
              onChange={setRotation}
              min={-45}
              max={45}
              step={1}
              marks={[
                { value: -45, label: '' },
                { value: -22.5, label: '' },
                { value: 0, label: '' },
                { value: 22.5, label: '' },
                { value: 45, label: '' },
              ]}
              color={primaryColor}
              size="sm"
              data-testid="crop-rotation-slider"
            />
            </Box>

            {/* Actions */}
            <Group justify="center" gap="xl" mt="md" wrap="nowrap">
              <Stack align="center" gap={4} onClick={handleRotate90} style={{ cursor: 'pointer' }} data-testid="crop-rotate-button">
                <IconRotateClockwise size={24} color="#333" stroke={1.5} />
                <Text size="xs" fw={700} c="#333">ROTATE</Text>
              </Stack>
              <Stack align="center" gap={4} onClick={handleFlip} style={{ cursor: 'pointer' }} data-testid="crop-flip-button">
                <IconFlipHorizontal size={24} color={flipHorizontal ? primaryColor : "#333"} stroke={1.5} />
                <Text size="xs" fw={700} c={flipHorizontal ? primaryColor : "#333"}>FLIP</Text>
              </Stack>
            </Group>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};
